import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Send } from 'lucide-react';
import { API_BASE } from '../../../config/api';

const API = `${API_BASE}/api`;
const chip = (on) => `px-3 py-1.5 rounded-full text-xs border transition-colors ${on ? 'bg-navigate-gold text-black border-navigate-gold' : 'border-zinc-700 text-zinc-300 hover:border-zinc-500'}`;

export default function ComposeEmailCard({ headers, onSent }) {
  const [users, setUsers] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [audience, setAudience] = useState('users');
  const [picked, setPicked] = useState([]);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({ subject: '', message: '' });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/users?limit=1000`, { headers }).then((r) => setUsers(r.data.users || [])).catch(() => {});
    axios.get(`${API}/admin/cohorts`, { headers }).then((r) => setCohorts(r.data.cohorts || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shown = useMemo(() => users.filter((u) => `${u.full_name} ${u.email}`.toLowerCase().includes(search.toLowerCase())).slice(0, 40), [users, search]);
  const toggle = (id) => setPicked(picked.includes(id) ? picked.filter((x) => x !== id) : [...picked, id]);
  const switchAudience = (a) => { setAudience(a); setPicked([]); };

  const send = async (e) => {
    e.preventDefault();
    if (audience === 'all' && !window.confirm('Email every active user on the platform?')) return;
    setSending(true);
    try {
      const body = { ...form, audience, user_ids: audience === 'users' ? picked : [], cohort_ids: audience === 'cohorts' ? picked : [] };
      const r = await axios.post(`${API}/admin/email/send`, body, { headers });
      toast.success(`Sending to ${r.data.recipient_count} recipient(s)`);
      setForm({ subject: '', message: '' });
      setPicked([]);
      onSent();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not send'); }
    finally { setSending(false); }
  };

  return (
    <form onSubmit={send} className="bg-navigate-surface border border-zinc-800 rounded-xl p-6 space-y-4" data-testid="compose-email-card">
      <h2 className="text-lg font-semibold text-white flex items-center gap-2"><Mail className="h-5 w-5 text-navigate-gold" />Compose email</h2>
      <div className="flex gap-2">
        {[['users', 'Selected users'], ['cohorts', 'Cohorts'], ['all', 'All users']].map(([id, label]) => (
          <button type="button" key={id} onClick={() => switchAudience(id)} className={chip(audience === id)} data-testid={`email-audience-${id}`}>{label}</button>
        ))}
      </div>
      {audience === 'users' && (
        <div className="space-y-2">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search users by name or email" className="w-full h-9 bg-navigate-dark border border-zinc-700 rounded-lg text-sm text-white px-3" data-testid="email-user-search" />
          <div className="max-h-44 overflow-y-auto flex flex-wrap gap-2" data-testid="email-user-list">
            {shown.map((u) => <button type="button" key={u.user_id} onClick={() => toggle(u.user_id)} className={chip(picked.includes(u.user_id))} data-testid={`email-user-${u.email}`}>{u.full_name || u.email}</button>)}
          </div>
          <p className="text-xs text-zinc-500">{picked.length} selected</p>
        </div>
      )}
      {audience === 'cohorts' && (
        cohorts.length === 0 ? <p className="text-xs text-amber-300">No cohorts yet — create them in the Cohorts section first.</p> : (
          <div className="flex flex-wrap gap-2">
            {cohorts.map((c) => <button type="button" key={c.cohort_id} onClick={() => toggle(c.cohort_id)} className={chip(picked.includes(c.cohort_id))} data-testid={`email-cohort-${c.cohort_id}`}>{c.name} ({c.member_count})</button>)}
          </div>
        )
      )}
      <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Subject" maxLength={200} className="w-full h-10 bg-navigate-dark border border-zinc-700 rounded-lg text-sm text-white px-3" data-testid="email-subject-input" />
      <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Message" rows={6} className="w-full bg-navigate-dark border border-zinc-700 rounded-lg text-sm text-white p-3" data-testid="email-message-input" />
      <button type="submit" disabled={sending || !form.subject.trim() || !form.message.trim() || (audience !== 'all' && !picked.length)} className="btn-gold px-5 py-2 text-sm flex items-center gap-2 disabled:opacity-50" data-testid="send-email-btn">
        <Send className="h-4 w-4" />{sending ? 'Sending...' : 'Send email'}
      </button>
    </form>
  );
}
